import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Star, ShoppingCart, Heart } from "lucide-react";
import { toast } from "sonner";
import { useCart } from "../context/CartContext";
import { useFavorites } from "../context/FavoritesContext";
import { fmtPrice, FREE_SHIPPING } from "../lib/shop";

export function AromaBadge({ aroma }) {
  if (!aroma) return null;
  const isOcean = /ocean|neutr/i.test(aroma);
  return (
    <span
      className={`rounded-full px-3 py-1 text-xs font-bold uppercase tracking-[0.08em] ${isOcean ? "bg-navy text-white" : "bg-sky2 text-navy"}`}
    >
      {aroma}
    </span>
  );
}

export function ProductCard({ product, index = 0 }) {
  const { addItem } = useCart();
  const { isFav, toggle } = useFavorites();
  const outOfStock = product.stock <= 0;
  const fav = isFav(product.id);
  const discount = product.old_price > product.price
    ? Math.round((1 - product.price / product.old_price) * 100)
    : 0;

  const handleAdd = () => {
    addItem(product);
    toast.success("Adăugat în coș", { description: product.name });
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 28 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-40px" }}
      transition={{ duration: 0.55, delay: (index % 3) * 0.08, ease: [0.22, 1, 0.36, 1] }}
      className="group flex flex-col overflow-hidden rounded-lg border border-line bg-white transition-shadow duration-300 hover:shadow-[0_1px_2px_rgba(27,75,122,0.08),0_8px_24px_rgba(27,75,122,0.10)]"
      data-testid={`product-card-${product.id}`}
    >
      <Link
        to={`/produs/${product.id}`}
        className="relative block aspect-square overflow-hidden bg-skytint"
        data-testid={`product-link-${product.id}`}
      >
        <img
          src={product.image}
          alt={product.name}
          loading="lazy"
          className="h-full w-full object-cover transition-transform duration-700 ease-out group-hover:scale-105"
        />
        <div className="absolute left-3 top-3 flex flex-col items-start gap-1.5">
          {product.aroma && <AromaBadge aroma={product.aroma} />}
          {product.tag && (
            <span className="rounded-full border border-line bg-white px-3 py-1 text-xs font-bold uppercase tracking-[0.08em] text-navy">
              {product.tag}
            </span>
          )}
        </div>
        {discount > 0 && (
          <span
            className="absolute right-3 top-3 flex h-12 w-12 items-center justify-center rounded-full bg-navy text-xs font-extrabold text-white"
            data-testid={`discount-badge-${product.id}`}
          >
            -{discount}%
          </span>
        )}
      </Link>
      <div className="flex flex-1 flex-col gap-2.5 p-6">
        <span className="text-sm font-bold uppercase tracking-[0.08em] text-muted2">
          {product.category}
        </span>
        <Link to={`/produs/${product.id}`}>
          <h3 className="font-display text-xl font-bold leading-snug text-navy">
            {product.name}
          </h3>
        </Link>
        <div className="flex items-center gap-1.5 text-sm text-muted2">
          <span className="flex items-center gap-0.5 text-amber-400">
            {Array.from({ length: 5 }).map((_, i) => (
              <Star
                key={i}
                className={`h-4 w-4 ${i < Math.round(product.rating) ? "fill-amber-400" : "fill-line text-line"}`}
              />
            ))}
          </span>
          <span className="font-semibold text-ink">{product.rating}</span>
          <span>({product.reviews_count})</span>
        </div>
        <span className="text-base text-muted2">{product.volume}</span>
        <div className="mt-auto pt-2">
          <div className="flex items-end justify-between">
            <div>
              {product.old_price > product.price && (
                <span className="block text-sm text-muted2 line-through">
                  {fmtPrice(product.old_price)}
                </span>
              )}
              <span className="font-display text-3xl font-bold text-navy" data-testid={`product-price-${product.id}`}>
                {fmtPrice(product.price)}
              </span>
              <span className={`block text-sm font-semibold ${outOfStock ? "text-rose-600" : product.stock < 15 ? "text-amber-600" : "text-navy"}`}>
                {outOfStock ? "Stoc epuizat" : product.stock < 15 ? "Stoc limitat" : "În stoc"}
              </span>
            </div>
            <button
              onClick={() => toggle(product.id)}
              className={`flex h-12 w-12 items-center justify-center rounded-lg border border-line bg-white transition-colors ${fav ? "text-rose-500" : "text-muted2 hover:text-rose-500"}`}
              aria-label={fav ? "Elimină de la favorite" : "Adaugă la favorite"}
              data-testid={`favorite-toggle-${product.id}`}
            >
              <Heart className={`h-5 w-5 ${fav ? "fill-rose-500" : ""}`} />
            </button>
          </div>
          <button
            onClick={handleAdd}
            disabled={outOfStock}
            data-testid={`add-to-cart-${product.id}`}
            className="mt-3 flex h-12 w-full items-center justify-center gap-2 rounded-lg bg-navy text-base font-bold text-white transition-colors hover:bg-navy-dark active:scale-[0.98] disabled:cursor-not-allowed disabled:bg-line"
          >
            <ShoppingCart className="h-5 w-5" />
            {outOfStock ? "Indisponibil" : "Adaugă în coș"}
          </button>
          {discount > 0 && (
            <p className="mt-2 text-center text-xs font-bold text-navy">
              Economie de {fmtPrice(product.old_price - product.price)} · Transport gratuit peste {FREE_SHIPPING} lei
            </p>
          )}
        </div>
      </div>
    </motion.div>
  );
}
